import { useCallback, useEffect, useRef, useState } from 'react';
import { Paper, Typography } from '@mui/material';
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import { getMacieConfig } from '../../../services/macieApi';
import MacieSetup from './MacieSetup';
import MacieDashboard from './MacieDashboard';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState, PortalSectionHeader } from '../../../components/portal/PortalStates';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import { CK, FONT_BODY, sectionCardSx } from '../portalStyles';

const MACIE_COLOR = '#0d9488';

// Every Macie state (loading, error, transitioning) keeps the same page header
// mounted so the layout never jumps as the config lands.
const macieHeader = (
  <PortalPageHeader
    icon={<FindInPageOutlinedIcon sx={{ fontSize: 24 }} />}
    title="Macie"
    subtitle="Amazon Macie S3 data-security posture"
    color={MACIE_COLOR}
  />
);

// MacieEntry decides what to render at /securityhub/macie. Flow:
//   no config / disabled / failed  → setup wizard
//   in_progress                    → transitioning view (polls until done)
//   enabled / partial_failure      → Macie dashboard
//
// The "in_progress" branch handles both initial enable (after the wizard
// submits) AND teardown initiated from Settings → Danger Zone, which
// navigates here so this component's polling drives the UI forward.
export default function MacieEntry() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const refresh = useCallback(async ({ showSpinner = true } = {}) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await getMacieConfig();
      setConfig(res?.data || null);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll during any in_progress transition (enable or disable) so the view
  // advances automatically once the orchestrator goroutine finishes.
  useEffect(() => {
    if (config?.status !== 'in_progress') return undefined;
    pollRef.current = setInterval(() => refresh({ showSpinner: false }), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [config?.status, refresh]);

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{macieHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading Macie…" color={MACIE_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{macieHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => refresh()} />
        </motion.div>
      </motion.div>
    );
  }

  if (config?.status === 'in_progress') {
    const hint = (config.errorMessage || '').toLowerCase();
    let title = 'Enabling Macie across accounts and regions…';
    let body = 'Enrolling member accounts, enabling Macie sessions, and inviting members from the administrator account. This can take a few minutes.';
    if (hint.includes('disabl')) {
      title = 'Disabling Macie…';
      body = 'Tearing down members and Macie sessions across every linked region. This can take a few minutes.';
    } else if (hint.includes('discovery')) {
      title = 'Updating automated discovery…';
      body = 'Applying the automated sensitive-data discovery configuration across every member account and region.';
    } else if (hint.includes('region')) {
      title = 'Updating linking regions…';
      body = 'Enrolling or tearing down Macie in the requested regions across every member account.';
    }
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{macieHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalSectionHeader
              icon={<FindInPageOutlinedIcon sx={{ fontSize: 20 }} />}
              title={title}
              color={MACIE_COLOR}
              mb={1.5}
            />
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
              {body}
            </Typography>
            <OperationProgress percent={config.progress} label={config.progressLabel || 'Working…'} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // Terminal "not usably configured" states — surface the setup wizard so the
  // user can re-run. EnableMacie clears any prior failed/disabled row
  // server-side before creating a new one.
  if (!config || config.status === 'disabled' || config.status === 'failed') {
    return (
      <MacieSetup
        onComplete={refresh}
        priorFailure={config?.status === 'failed' ? config.errorMessage : null}
      />
    );
  }

  return <MacieDashboard config={config} onConfigChange={refresh} />;
}
